/**
 * What the lobby *shows*, derived from `LobbyInfo` — pure, so the roster rules
 * are unit-testable without a DOM or a socket (mahjong's Vitest runs in node).
 * `OnlineLobbyScreen.ts` does nothing but localize and paint what comes out of
 * here.
 *
 * The table is always four seats. The *room* only sizes to the humans (plan
 * D1), so every seat past `humanSeats` is a bot from the first deal — and a
 * human seat nobody has taken yet becomes one too the moment the host presses
 * "Start now". That is why an unclaimed human seat reads as `'open'` rather
 * than as `'bot'`: it is still a seat a friend can walk into.
 */

import { SEAT_COUNT } from '../../game/types.js';
import type { LobbyState } from '@bn-games/room-client';
import type { SeatedPlayer } from '../../network/protocol.js';

export type LobbySeatKind = 'you' | 'player' | 'open' | 'bot';

export interface LobbySeatRow {
  /** Table seat index, `0..SEAT_COUNT - 1`. */
  readonly seat: number;
  readonly kind: LobbySeatKind;
  /** The name the peer joined under; `null` for an open or bot seat. */
  readonly name: string | null;
}

export interface LobbyView {
  /**
   * Seats held for humans: the server's `LobbyInfo.seatCount` once it has said,
   * and the host's own pick before that (`ROOM_CREATED` carries no roster).
   */
  readonly humanSeats: number;
  readonly localSeat: number | null;
  readonly players: readonly SeatedPlayer[] | null;
  /** True on the client that created the room — slot 0, the only early starter. */
  readonly isHost: boolean;
}

/** The creator always lands in room slot 0 — `RoomServer` seats them first. */
const HOST_SLOT = 0;

export function buildSeatRoster(view: LobbyView): readonly LobbySeatRow[] {
  const rows: LobbySeatRow[] = [];
  for (let seat = 0; seat < SEAT_COUNT; seat += 1) {
    if (seat >= view.humanSeats) {
      rows.push({ seat, kind: 'bot', name: null });
      continue;
    }
    const player = view.players?.find((p) => p.seat === seat);
    if (player !== undefined) {
      rows.push({ seat, kind: seat === view.localSeat ? 'you' : 'player', name: player.name });
      continue;
    }
    // Before GAME_START the host has no roster at all (`OPPONENT_JOINED` is a
    // 2-seat shorthand and mahjong rooms are bigger), but it does know that it
    // is itself sitting in slot 0.
    const isSelf = view.isHost && seat === HOST_SLOT && view.localSeat === null;
    rows.push({ seat, kind: isSelf ? 'you' : 'open', name: null });
  }
  return rows;
}

/**
 * Seated and waiting for the table to fill. `waiting_for_opponent` is the
 * creator's view; a seat taken by code, link, or a pre-game reload sits in
 * `joining` with its seat already assigned (`ROOM_JOINED` sets `localSeat`).
 */
export function isSeatedWaiting(view: LobbyView, state: LobbyState): boolean {
  return state === 'waiting_for_opponent' || (state === 'joining' && view.localSeat !== null);
}

/**
 * "Start now" is host-only: any other seat gets an `ERROR` back, and the server
 * drops the message once a session exists — so it is offered exactly while
 * the host is seated and the room hasn't started.
 */
export function canStartNow(view: LobbyView, state: LobbyState): boolean {
  return view.isHost && isSeatedWaiting(view, state);
}
